import React from 'react';

import { Divider, Typography } from '@mui/material';
import { AlineMarkdownEditor } from 'src/components/ai/chat/ChatPanel/AlineMarkdownEditor/AlineMarkdownEditor';
import { makeStyles } from 'tss-react/mui';

import type { EmailThread, EmailThreadMessage } from './email-thread';
import { getAddressDisplayName, getIsAssistantAddress } from './email-thread';
import { EmailAttachmentCard } from './EmailAttachmentCard';
import { EmailParticipantAvatar } from './EmailParticipantAvatar';

const BODY_TEXT_STYLE: React.CSSProperties = { fontSize: 12, lineHeight: '18px' };

const useStyles = makeStyles<{ spacious: boolean }>()((theme, { spacious }) => ({
  addresses: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacious ? 12 : 8,
  },
  attachmentList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  attachments: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    paddingTop: spacious ? 14 : 8,
  },
  body: {
    color: theme.appColors.iconsType,
    paddingBottom: spacious ? 16 : 8,
    paddingTop: spacious ? 24 : 8,
  },
  divider: {
    borderColor: theme.appColors.ruleSubtle,
  },
  identity: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minWidth: 0,
    overflowWrap: 'anywhere',
  },
  label: {
    flexShrink: 0,
    width: 40,
  },
  muted: {
    color: theme.palette.text.secondary,
  },
  name: {
    fontWeight: 600,
  },
  participant: {
    alignItems: 'flex-start',
    display: 'flex',
    gap: 8,
  },
}));

interface EmailMessageDetailsProps {
  message: EmailThreadMessage;
  thread: EmailThread;
  /** The roomier spacing of a thread's only message. */
  spacious?: boolean;
}

/** Who an email went between, what it said, and the files it carried. */
export const EmailMessageDetails = React.memo(function EmailMessageDetails({
  message,
  spacious = false,
  thread,
}: EmailMessageDetailsProps) {
  const { classes } = useStyles({ spacious });
  const attachmentCount = message.attachments.length;

  return (
    <div>
      <div className={classes.addresses}>
        <EmailParticipant
          address={message.fromAddress}
          isAssistant={message.isAiAuthored}
          label="From"
          name={message.senderName}
          spacious={spacious}
        />
        <EmailRecipients
          addresses={message.toAddresses}
          label="To"
          spacious={spacious}
          thread={thread}
        />
        <EmailRecipients
          addresses={message.ccAddresses}
          label="Cc"
          spacious={spacious}
          thread={thread}
        />
      </div>
      <div className={classes.body}>
        <AlineMarkdownEditor
          noBorder
          readOnly
          maxHeight="none"
          minHeight={0}
          textareaStyle={BODY_TEXT_STYLE}
          value={message.text}
        />
      </div>
      {attachmentCount > 0 && (
        <>
          <Divider className={classes.divider} />
          <div className={classes.attachments}>
            <Typography className={classes.muted} variant="caption">
              {attachmentCount === 1 ? '1 attachment' : `${attachmentCount} attachments`}
            </Typography>
            <div className={classes.attachmentList}>
              {message.attachments.map(attachment => (
                <EmailAttachmentCard key={attachment.documentId} attachment={attachment} />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
});

function EmailRecipients({
  addresses,
  label,
  spacious,
  thread,
}: {
  addresses: string[];
  label: string;
  spacious: boolean;
  thread: EmailThread;
}): JSX.Element {
  return (
    <>
      {addresses.map((address, index) => (
        <EmailParticipant
          key={address}
          address={address}
          isAssistant={getIsAssistantAddress(thread, address)}
          label={index === 0 ? label : ''}
          name={getAddressDisplayName(thread, address)}
          spacious={spacious}
        />
      ))}
    </>
  );
}

function EmailParticipant({
  address,
  isAssistant,
  label,
  name,
  spacious,
}: {
  address: string | null;
  isAssistant: boolean;
  label: string;
  name: string;
  spacious: boolean;
}): JSX.Element {
  const { classes, cx } = useStyles({ spacious });

  return (
    <div className={classes.participant}>
      <Typography className={cx(classes.label, classes.muted)} variant="caption">
        {label}
      </Typography>
      <EmailParticipantAvatar
        colorKey={address ?? name}
        isAssistant={isAssistant}
        name={name}
        size="xs"
      />
      <span className={classes.identity}>
        <Typography className={classes.name} variant="caption">
          {name}
        </Typography>
        {address && address !== name && (
          <Typography className={classes.muted} variant="caption">
            {address}
          </Typography>
        )}
      </span>
    </div>
  );
}
